/**
 * Internal Hook: useSettingsQuery
 *
 * Fetches profile settings from backend using TanStack Query.
 * Following the pattern from ScheduledMeetingsContext.
 */

import { useQuery } from '@tanstack/react-query';
import { useApiManager } from '../../../hooks';
import { useUser } from '../../UserContext';
import { useUserPermissions } from '../../UserPermissionsContext';
import { SETTINGS_QUERY_KEYS } from '../helpers';
import type { SettingsContextConfig } from '../helpers/config';
import type { ProfileData } from '../types';

export const useSettingsQuery = (config: SettingsContextConfig) => {
  const apiManager = useApiManager();
  const { isSuccess: isUserPermissionsReady } = useUserPermissions();
  const { isAuthenticated } = useUser();

  return useQuery<ProfileData, Error>({
    queryKey: SETTINGS_QUERY_KEYS.settings,
    queryFn: async () => {
      if (!(apiManager && isUserPermissionsReady)) {
        throw new Error('API Manager not initialized');
      }
      return await apiManager.profile.fetchProfileData();
    },
    enabled: isAuthenticated && isUserPermissionsReady && config.autoFetch,
    staleTime: config.staleTime,
    gcTime: config.cacheTime,
    refetchOnWindowFocus: config.refetchOnWindowFocus,
    refetchOnReconnect: config.refetchOnReconnect,
  });
};
